import api from './api';

export type VocabCardLang = 'pl' | 'en' | 'de' | 'uk' | 'it' | 'fr' | 'es';
export type VocabCardCefr = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export interface VocabCardCategory {
	id: number;
	title: string;
}

export interface VocabCardAdminItem {
	id: number;
	source_lang: VocabCardLang;
	target_lang: VocabCardLang;
	term: string;
	translation: string;
	definition: string | null;
	definition_translation: string | null;
	example_sentence: string | null;
	example_translation: string | null;
	audio_path: string | null;
	audio_duration_seconds: number | null;
	image_url: string | null;
	cefr_level: VocabCardCefr | null;
	like_count: number;
	is_active: boolean;
	created_by: number | null;
	last_edited_by: number | null;
	created_at: string;
	updated_at: string;
	categories: VocabCardCategory[];
}

export interface VocabCardListResponse {
	items: VocabCardAdminItem[];
	pagination: {
		page: number;
		page_size: number;
		total: number;
	};
}

export interface VocabCardListParams {
	page?: number;
	page_size?: number;
	q?: string;
	source_lang?: VocabCardLang;
	cefr_level?: VocabCardCefr;
	category_id?: number | string;
	is_active?: boolean | string;
}

export interface UpsertVocabCardPayload {
	source_lang: VocabCardLang;
	target_lang: VocabCardLang;
	term: string;
	translation: string;
	definition?: string | null;
	definition_translation?: string | null;
	example_sentence?: string | null;
	example_translation?: string | null;
	audio_path?: string | null;
	audio_duration_seconds?: number | null;
	image_url?: string | null;
	cefr_level?: VocabCardCefr | null;
	is_active?: boolean;
	category_ids?: number[];
}

export interface GenerateAudioResponse {
	ok: boolean;
	audio: {
		id: number;
		audio_path: string | null;
		audio_duration_seconds: number | null;
	};
}

export interface GenerateBulkAudioPayload {
	card_ids?: number[];
	limit?: number;
	source_lang?: VocabCardLang;
	cefr_level?: VocabCardCefr | null;
	is_active?: boolean;
	overwrite_existing?: boolean;
}

export interface GenerateBulkAudioItemResult {
	card_id: number;
	status: 'ok' | 'error';
	audio?: {
		id: number;
		audio_path: string | null;
		audio_duration_seconds: number | null;
	};
	error?: string;
}

export interface GenerateBulkAudioResponse {
	ok: boolean;
	overwrite_existing: boolean;
	total_requested: number;
	total_selected: number;
	success_count: number;
	error_count: number;
	missing_card_ids: number[];
	skipped_existing_audio_ids: number[];
	items: GenerateBulkAudioItemResult[];
}

export interface VocabCardImportItem {
	source_lang?: VocabCardLang;
	target_lang?: VocabCardLang;
	term: string;
	translation: string;
	definition?: string | null;
	definition_translation?: string | null;
	example_sentence?: string | null;
	example_translation?: string | null;
	audio_path?: string | null;
	audio_duration_seconds?: number | null;
	image_url?: string | null;
	cefr_level?: VocabCardCefr | null;
	is_active?: boolean;
	category_ids?: number[];
}

export interface VocabCardImportPayload {
	dry_run?: boolean;
	update_existing?: boolean;
	defaults?: {
		source_lang?: VocabCardLang;
		target_lang?: VocabCardLang;
		cefr_level?: VocabCardCefr | null;
		is_active?: boolean;
		category_ids?: number[];
	};
	items: VocabCardImportItem[];
}

export interface VocabCardImportRowResult {
	index: number;
	term: string | null;
	source_lang: string | null;
	cefr_level: string | null;
	status:
		| 'created'
		| 'updated'
		| 'skipped_existing'
		| 'would_create'
		| 'would_update'
		| 'error';
	id?: number | null;
	error?: string;
}

export interface VocabCardImportResponse {
	ok: boolean;
	dry_run: boolean;
	update_existing: boolean;
	total: number;
	created: number;
	updated: number;
	skipped: number;
	errors: number;
	results: VocabCardImportRowResult[];
}

export async function getAdminVocabCards(
	params: VocabCardListParams = {}
): Promise<VocabCardListResponse> {
	const { data } = await api.get('/vocab-cards/admin/cards', { params });
	return data;
}

export async function getAdminVocabCardById(id: number | string): Promise<VocabCardAdminItem> {
	const { data } = await api.get(`/vocab-cards/admin/cards/${id}`);
	return data;
}

export async function createAdminVocabCard(
	payload: UpsertVocabCardPayload
): Promise<VocabCardAdminItem> {
	const { data } = await api.post('/vocab-cards/admin/cards', payload);
	return data;
}

export async function updateAdminVocabCard(
	id: number | string,
	payload: Partial<UpsertVocabCardPayload>
): Promise<VocabCardAdminItem> {
	const { data } = await api.patch(`/vocab-cards/admin/cards/${id}`, payload);
	return data;
}

export async function deleteAdminVocabCard(id: number | string): Promise<{ ok: boolean }> {
	const { data } = await api.delete(`/vocab-cards/admin/cards/${id}`);
	return data;
}

export async function generateAdminVocabCardAudio(
	id: number | string
): Promise<GenerateAudioResponse> {
	const { data } = await api.post(`/vocab-cards/admin/cards/${id}/generate-audio`);
	return data;
}

export async function generateAdminVocabCardsAudioBulk(
	payload: GenerateBulkAudioPayload
): Promise<GenerateBulkAudioResponse> {
	const { data } = await api.post('/vocab-cards/admin/cards/generate-audio-bulk', payload);
	return data;
}

export async function importAdminVocabCardsJson(
	payload: VocabCardImportPayload
): Promise<VocabCardImportResponse> {
	const { data } = await api.post('/vocab-cards/admin/cards/import-json', payload);
	return data;
}